import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight, Phone } from "lucide-react";
import Logo from "./Logo";

function ContactButton({ settings }) {
    if (settings?.contact_button_enabled === false) return null;
    const label = settings?.contact_button_text || "Contact Us";
    const rawUrl = settings?.contact_button_url || "/contact";
    const style = settings?.contact_button_style || "primary";
    const isExternal = /^(https?:|mailto:|tel:)/i.test(rawUrl);
    const cls =
        style === "outline"
            ? "border border-lnl-green text-lnl-green hover:bg-lnl-green hover:text-white px-4 py-2 rounded-md text-[13px] font-heading font-semibold transition-colors"
            : style === "secondary"
                ? "lnl-btn-secondary text-xs px-3 py-2"
                : "lnl-btn-primary text-xs px-3 py-2";
    if (isExternal) {
        return (
            <a href={rawUrl} target={rawUrl.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                className={cls} data-testid="nav-contact-btn">
                {label}
            </a>
        );
    }
    return <Link to={rawUrl} className={cls} data-testid="nav-contact-btn">{label}</Link>;
}

const NAV = [
    { to: "/", label: "Home" },
    { to: "/admissions", label: "Admissions" },
    { to: "/academics", label: "Academics" },
    { to: "/tuition-scholarships", label: "Tuition & Scholarships" },
    { to: "/campus-life", label: "Campus Life" },
    { to: "/about", label: "About LNL" },
    { to: "/news", label: "News & Events" },
];

export default function Header({ settings }) {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [loc.pathname]);

    return (
        <header
            className={`sticky top-0 z-40 bg-white border-b border-lnl-border transition-shadow ${scrolled ? "shadow-sm" : ""}`}
            data-testid="site-header"
        >
            <div className="lnl-container flex items-center justify-between" style={{ height: scrolled ? 64 : 76 }}>
                <Link to="/" className="flex items-center gap-3" data-testid="header-home-link">
                    <Logo size={scrolled ? 40 : 48} />
                    <div className="hidden sm:block">
                        <div className="font-heading font-bold text-[15px] leading-tight text-lnl-charcoal">
                            {settings?.official_name || "Lyceum Northern Luzon"}
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.16em] text-lnl-gray leading-tight">
                            Urdaneta City · Est. {settings?.established_year_draft || "1994"}
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-5 xl:gap-6" aria-label="Primary">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={({ isActive }) =>
                                `whitespace-nowrap text-[13px] xl:text-[13.5px] font-heading font-semibold tracking-tight transition-colors ${
                                    isActive ? "text-lnl-green" : "text-lnl-charcoal hover:text-lnl-green"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                    <ContactButton settings={settings} />
                </nav>



                <div className="flex lg:hidden items-center gap-2">
                    <a
                        href={`tel:${(settings?.mobile || "").replace(/[^0-9+]/g, "")}`}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-md border border-lnl-border text-lnl-green-deep"
                        aria-label="Call Admissions"
                        data-testid="header-call"
                    >
                        <Phone className="h-4 w-4" />
                    </a>
                    <Link to="/admissions/apply" className="lnl-btn-primary text-xs px-3 py-2" data-testid="header-apply-mobile">
                        Apply
                    </Link>
                    <button
                        onClick={() => setOpen((o) => !o)}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-md border border-lnl-border"
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        data-testid="header-menu-toggle"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-lnl-border bg-white" data-testid="mobile-menu">
                    <nav className="lnl-container py-3" aria-label="Mobile primary">
                        {NAV.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                className="flex items-center justify-between py-3 border-b border-lnl-border/70 font-heading font-semibold text-[15px] text-lnl-charcoal"
                                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            >
                                {n.label}
                                <ChevronRight className="h-4 w-4 text-lnl-gray" />
                            </Link>
                        ))}
                        <div className="flex gap-2 pt-3">
                            <Link to="/contact" className="lnl-btn-secondary flex-1" data-testid="mobile-ask">Ask Admissions</Link>
                            <Link to="/admissions/apply" className="lnl-btn-primary flex-1" data-testid="mobile-enroll">Enroll Now</Link>
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
}
